import { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios'

declare module 'axios' {
  interface InternalAxiosRequestConfig {
    _retry?: boolean
  }
}

export interface RefreshInterceptorConfig {
  /** Perform the actual refresh; resolve when done, reject if the refresh failed. */
  refresh: () => Promise<void>
  /** Attach a fresh auth header to the failed request's config right before retrying it. */
  applyAuthHeader: (config: InternalAxiosRequestConfig) => InternalAxiosRequestConfig
  /** Decide whether this error should trigger a refresh-and-retry. */
  shouldRefresh: (error: AxiosError) => boolean
  /** Called once all queued requests should give up because the refresh itself failed. */
  onRefreshFailure: (error: unknown) => void
}

export function attachRefreshInterceptor(instance: AxiosInstance, cfg: RefreshInterceptorConfig) {
  let refreshPromise: Promise<void> | null = null

  instance.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const originalRequest = error.config as InternalAxiosRequestConfig | undefined

      if (!originalRequest || !cfg.shouldRefresh(error) || originalRequest._retry) {
        return Promise.reject(error)
      }

      originalRequest._retry = true

      if (!refreshPromise) {
        refreshPromise = cfg.refresh().finally(() => {
          refreshPromise = null
        })
      }

      try {
        await refreshPromise
        return instance(cfg.applyAuthHeader(originalRequest))
      } catch (refreshErr) {
        cfg.onRefreshFailure(refreshErr)
        return Promise.reject(error)
      }
    },
  )
}
